import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, MapPin, ShieldCheck } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ParallaxBg, FadeUp, ParallaxImage } from "@/components/Parallax";
import { useSEO } from "@/lib/useSEO";
import { findSeoPage } from "@/lib/seoPages";
import { IMG } from "@/lib/images";

export default function LocationPage() {
  const loc = useLocation();
  const slug = loc.pathname.replace(/^\//, "").split("/")[0];
  const page = findSeoPage(slug);

  const canonical = page ? `${window.location.origin}/${page.slug}` : window.location.href;
  const image = page ? (IMG[page.heroImage] || IMG.heroExterior) : IMG.heroExterior;

  const schema = page ? {
    "@context": "https://schema.org",
    "@type": page.kind === "location" ? "LocalBusiness" : "Service",
    name: "Southern Utah Event Rentals — Luxury Portable Restrooms",
    telephone: "+1-435-288-9909",
    email: "info@southernutaheventrentals.com",
    url: canonical,
    image,
    address: { "@type": "PostalAddress", addressRegion: "UT", addressCountry: "US", addressLocality: "St. George" },
    areaServed: page.localTerms,
    description: page.metaDescription,
    sameAs: [
      "https://www.instagram.com/luxuryportablerestroom",
      "https://www.facebook.com/share/1B5bf6BXyk/",
    ],
    ...(page.kind === "event" && {
      serviceType: page.h1,
      provider: { "@type": "LocalBusiness", name: "Southern Utah Event Rentals" },
    }),
  } : null;

  useSEO({
    title: page?.title,
    description: page?.metaDescription,
    canonical,
    image,
    schema,
  });

  if (!page) return <Navigate to="/" replace />;

  return (
    <div>
      {/* Hero */}
      <ParallaxBg image={image} minHeight="min-h-[80vh]" overlay="bg-gradient-to-b from-black/40 via-black/25 to-black/70" speed={0.25}>
        <div className="mx-auto max-w-6xl h-full px-6 md:px-10 lg:px-16 flex items-end pb-16 md:pb-24 min-h-[80vh]">
          <FadeUp className="max-w-3xl text-ivory">
            <div className="overline text-ivory/70">{page.kicker}</div>
            <h1 className="h-display mt-4 text-5xl md:text-7xl leading-[1.02]">{page.h1}</h1>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/booking" data-testid={`seo-book-btn-${page.slug}`} className="btn-primary">
                Request Your Trailer <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="tel:+14352889909" className="btn-secondary">
                <Phone className="h-4 w-4" /> 435.288.9909
              </a>
            </div>
          </FadeUp>
        </div>
      </ParallaxBg>

      {/* Intro */}
      <section className="section">
        <div className="mx-auto max-w-4xl">
          <FadeUp>
            <p className="text-lg md:text-xl text-graphite leading-relaxed">{page.intro}</p>
          </FadeUp>

          <FadeUp>
            <div className="mt-12 grid sm:grid-cols-3 gap-3">
              {page.localTerms.map((t) => (
                <div key={t} className="flex items-center gap-3 rounded-2xl border border-stone bg-white p-4">
                  <MapPin className="h-4 w-4 text-pebble shrink-0" />
                  <span className="text-sm text-charcoal">{t}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Highlights */}
      <section className="section bg-sand">
        <div className="mx-auto max-w-5xl grid md:grid-cols-2 gap-10 items-start">
          <ParallaxImage src={IMG.interiorSink} alt="Interior detail" className="h-[420px] rounded-3xl" speed={0.15} />
          <FadeUp>
            <div className="overline">Why customers choose us</div>
            <h2 className="h-display mt-3 text-3xl md:text-4xl leading-tight">What you can expect.</h2>
            <ul className="mt-8 space-y-4">
              {page.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <Check className="h-5 w-5 mt-0.5 text-pebble shrink-0" />
                  <span className="text-graphite leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-3 text-sm text-graphite">
              <ShieldCheck className="h-4 w-4 text-pebble" /> Professionally delivered · quiet generator · climate-controlled
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      {page.faq?.length > 0 && (
        <section className="section">
          <div className="mx-auto max-w-3xl">
            <FadeUp>
              <div className="overline">Frequently asked</div>
              <h2 className="h-display mt-3 text-3xl md:text-4xl">Quick answers.</h2>
            </FadeUp>
            <div className="mt-8 card-soft p-4 md:p-8">
              <Accordion type="single" collapsible className="w-full">
                {page.faq.map((f) => (
                  <AccordionItem key={f.q} value={f.q} className="border-b border-stone last:border-0">
                    <AccordionTrigger className="text-left text-base md:text-lg font-medium py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-graphite text-base leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section bg-charcoal text-ivory">
        <div className="mx-auto max-w-4xl text-center">
          <FadeUp>
            <div className="overline text-ivory/60">Ready when you are</div>
            <h2 className="h-display mt-4 text-4xl md:text-6xl leading-tight">Request your trailer.</h2>
            <p className="mt-5 text-ivory/75 max-w-2xl mx-auto">Tell us about your event and pay a small deposit — we'll confirm availability and coordinate the rest.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/booking" className="inline-flex items-center gap-2 rounded-full bg-champagne text-charcoal px-6 py-3 text-sm hover:bg-pebble transition-colors">
                Start your request <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="tel:+14352889909" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-ivory px-6 py-3 text-sm hover:bg-white/10 transition-colors">
                <Phone className="h-4 w-4" /> 435.288.9909
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
